import { BCDataGrid } from '@bimser/components';
import { isNullOrUndefined } from '@bimser/core';
import React from 'react';
import { ServiceList } from '../../services/serviceList';

export enum GridCacheSaveMethod {
	'localStorage',
	'sessionStorage',
	'server'
}

export interface IGridCacheSettingsOptions {
	filters: boolean;
	columnChoosing: boolean;
	columnSorting: boolean;
	columnWidth: boolean;
	pageIndex: boolean;
	pageSize: boolean;
	grouping: boolean;
	selections: boolean;
}

export interface IGridCacheSettings {
	enabled: boolean;
	method: GridCacheSaveMethod;
	settings: IGridCacheSettingsOptions;
}

declare global {
	interface Window {
		gridCacheSettings: IGridCacheSettings;
	}
}
export class GridSettingsHelper {
	public static setCacheSettings(gridCacheSettings: IGridCacheSettings) {
		window.gridCacheSettings = gridCacheSettings;
	}

	public static get getCacheSettings(): IGridCacheSettings {
		return window.gridCacheSettings;
	}

	public static get getCacheSettingsEnabled(): boolean {
		return window.gridCacheSettings?.enabled || false;
	}

	public static get getCacheSettingsMethod(): GridCacheSaveMethod {
		return window.gridCacheSettings?.method || GridCacheSaveMethod.localStorage;
	}

	public static get getCacheSettingsOptions(): IGridCacheSettingsOptions {
		return window.gridCacheSettings?.settings;
	}

	public static setSettings(gridId: string, settings: string) {
		if (!controlGridStateStoring()) {
			return;
		}

		ServiceList.web.GridSettings.SetGridSettings.callPromise(
			{
				gridId: gridId,
				settings: settings
			},
			true
		);
	}

	public static getSettings(gridId: string): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			if (!controlGridStateStoring()) {
				return resolve(null);
			}
			ServiceList.web.GridSettings.GetGridSettings.callPromise({ gridId: gridId }, false)
				.then(response => {
					resolve(response.settings);
				})
				.catch(err => {
					console.log(err);
					resolve(null);
				});
		});
	}

	public static removeSettings(gridId?: string) {
		if (!controlGridStateStoring()) {
			return;
		}
		ServiceList.web.GridSettings.DeleteGridSettings.callPromise({ gridId: gridId }, true);
	}

	public static removeTableCacheSettings(storageKey: string) {
		try {
			switch (GridSettingsHelper.getCacheSettingsMethod) {
				case GridCacheSaveMethod.localStorage:
					localStorage.removeItem(storageKey);
					break;
				case GridCacheSaveMethod.sessionStorage:
					sessionStorage.removeItem(storageKey);
					break;
				case GridCacheSaveMethod.server:
					ServiceList.web.GridSettings.DeleteGridSettings.callPromise(
						{ gridId: storageKey },
						true
					);
					break;
			}
		} catch (error) {
			console.log(error);
		}
	}

	public static setTableCacheSettings(storageKey: string, settings: string) {
		try {
			if (!GridSettingsHelper.getCacheSettingsEnabled) return;
			switch (GridSettingsHelper.getCacheSettingsMethod) {
				case GridCacheSaveMethod.localStorage:
					localStorage.setItem(storageKey, settings);
					break;
				case GridCacheSaveMethod.sessionStorage:
					sessionStorage.setItem(storageKey, settings);
					break;
				case GridCacheSaveMethod.server:
					ServiceList.web.GridSettings.SetGridSettings.callPromise(
						{
							gridId: storageKey,
							settings: settings
						},
						true
					);
					break;
			}
		} catch (error) {
			console.log(error);
		}
	}

	public static getDataControl = (
		settings: any,
		dataGridRef: React.RefObject<BCDataGrid.Entities.BCDataGridRef>
	): any => {
		const options = GridSettingsHelper.getCacheSettings.settings;

		if (isNullOrUndefined(settings) || !GridSettingsHelper.getCacheSettingsEnabled) return {};
		const tableSettings = JSON.parse(settings);

		tableSettings?.columns?.forEach(column => {
			const staticColumn = dataGridRef.current.instance().columnOption(column.name);

			if (staticColumn) {
				if (!options.filters) {
					column['selectedFilterOperation'] = staticColumn.selectedFilterOperation;
					column['filterValue'] = staticColumn.filterValue;
					column['filterValues'] = staticColumn.filterValues;
					column['filterType'] = staticColumn.filterType;
				}

				if (!options.columnChoosing) {
					column['visible'] = staticColumn.visible;
					column['visibleIndex'] = staticColumn.visibleIndex;
				}

				if (!options.columnSorting) {
					column['sortOrder'] = staticColumn.sortOrder;
					column['sortIndex'] = staticColumn.sortIndex;
				}

				if (!options.columnWidth) column['width'] = staticColumn.width;

				if (!options.grouping) column['groupIndex'] = staticColumn.groupIndex;
			}
		});

		if (!options.pageIndex)
			tableSettings['pageIndex'] =
				dataGridRef.current.instance().option('paging')?.pageIndex || 0;

		if (!options.pageSize)
			tableSettings['pageSize'] =
				dataGridRef.current.instance().option('paging')?.pageSize || 10;

		if (!options.selections)
			tableSettings['selectedRowKeys'] = dataGridRef.current.instance().getSelectedRowKeys();

		return tableSettings;
	};

	public static getTableCacheSettings(
		storageKey: string,
		dataGridRef: React.RefObject<BCDataGrid.Entities.BCDataGridRef>
	): Promise<any> {
		return new Promise<any>((resolve, reject) => {
			try {
				if (!GridSettingsHelper.getCacheSettingsEnabled) resolve({});

				switch (GridSettingsHelper.getCacheSettingsMethod) {
					case GridCacheSaveMethod.localStorage:
						resolve(
							GridSettingsHelper.getDataControl(
								localStorage.getItem(storageKey),
								dataGridRef
							)
						);
						break;
					case GridCacheSaveMethod.sessionStorage:
						resolve(
							GridSettingsHelper.getDataControl(
								sessionStorage.getItem(storageKey),
								dataGridRef
							)
						);
						break;
					case GridCacheSaveMethod.server:
						ServiceList.web.GridSettings.GetGridSettings.callPromise(
							{ gridId: storageKey },
							false
						)
							.then(response => {
								resolve(
									GridSettingsHelper.getDataControl(
										response.settings,
										dataGridRef
									)
								);
							})
							.catch(err => {
								console.log(err);
								resolve({});
							});
						break;
				}
			} catch (error) {
				console.log(error);
				resolve({});
			}
		});
	}
}

function controlGridStateStoring() {
	return localStorage.getItem('gridStateStoringEnabled') === 'true';
}
